import {
  DateField,
  ClinicalDataStatus,
  InlineOptionPicker,
  ListRowSkeleton,
  MoonFilterChip,
  MoonSearchInput,
  BackLink,
  Dialog,
  DialogActions,
  DialogHeader,
  PrimaryButton,
  ActionBanner,
  CompactActionButton,
  MoonValueInput,
  cardSurface,
  DialogIcon,
  EmptyState,
} from "@louise/moon-ui";
import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import resultHistoryIllustration from "../../assets/images/document-review-icons/source-documents-3d.png";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import { getBiomarkerFactsheet } from "@src/packages/test-results/content/biomarker-factsheets";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Animated,
  Easing,
} from "react-native";
import {
  AlertCircle,
  Calendar,
  ClipboardList,
  Dna,
  Edit3,
  FileText,
  FlaskConical,
  Microscope,
  Plus,
  ScanLine,
  TestTube2,
  Upload,
  type LucideIcon,
} from "lucide-react-native";
import { CommonActions } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import type { Locale } from "@src/packages/i18n/locale";
import type { CareStackParamList } from "../types";
import { useSmartBack } from "../hooks/useSmartBack";
import { EMBER } from "../../onboarding/theme";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import { LabValueField } from "@src/packages/test-results/components/LabValueField";
import type { LabResult } from "./care/sections/ResultsLabsCard";
import TestResultDetailDialog from "../components/TestResultDetailDialog";
import {
  useTestResults,
  useDeleteTestResult,
  useUpdateTestResult,
  useCreateTestResult,
  type TestResult,
  type TestResultType,
} from "@src/packages/test-results/hooks/useTestResults";
import { groupCanonicalTestResults } from "@src/packages/test-results";
import {
  CYCLE_TYPE_LABELS,
  useCycles,
  useCycleTypeLabels,
  type Cycle,
  type CycleType,
} from "@src/packages/cycles/hooks/useCycles";
import type { ResultInterpretation } from "@src/packages/homepage/plan/intelligence/useTestResultInsights";
import {
  isCycleContextSensitiveTest,
  type ResultInterpretationContext,
} from "@src/packages/test-results/interpretation-context";
import {
  useResultCategoryResolver,
  type ResultCategoryResolver,
} from "@src/packages/test-results/hooks/useResultCategory";
import {
  DocumentTestResultRow,
  type DocumentRowBadge,
} from "./documents/DocumentRow";
import DocumentUploadModal, {
  DocumentFileDropZone,
  type SelectedFile,
} from "@src/packages/document-vault/DocumentUploadModal";
import { getDevClinicalDataState } from "@src/packages/care/dev-clinical-data-state";
import {
  DEV_PREVIEW_AMH_RESULT,
  getDevPreviewCategory,
} from "../dev/labResultsFixture";
import {
  getCanonicalTestIdForDataPoint,
  getCanonicalTestIdForMarkerName,
  getTestRouteTarget,
} from "./access/testRouteIds";
import {
  getDataPointForLoincCode,
  getDataPointSampleTypes,
} from "@src/packages/content/biomarkers";
import { getTodayString } from "@src/packages/utils/date";

import { usePressCapture } from "@src/packages/analytics/usePressCapture";
type Props = NativeStackScreenProps<CareStackParamList, "AllLabResults"> & {
  results?: LabResult[];
};

type DisplayLabResult = LabResult & {
  sourceType?: TestResultType | null;
  cycleId?: string | null;
  cycleLabel?: string | null;
};

type LabResultTypeFilterId =
  | "all"
  | "blood"
  | "imaging"
  | "genetics"
  | "urine"
  | "semenFluidAnalysis"
  | "pathology"
  | "other";

type LabResultStatusFilterId = "all" | "resulted" | "pending" | "needs_review";

type LabResultCycleFilterId =
  "all" | "current" | "unlinked" | `cycle:${string}`;

type LabResultFilterOption<TId extends string = string> = {
  id: TId;
  label: string;
  icon?: LucideIcon;
};

type LabResultFilterChipOption = LabResultFilterOption & {
  selected: boolean;
  onPress: () => void;
};

function getDevPreviewLabResultsEnabled() {
  if (!__DEV__ || typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("dev_preview_lab_results") === "true" ||
      params.get("dev_preview_all_cards") === "true"
    );
  } catch {
    return false;
  }
}

function getDevPreviewLabResultsState() {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const state = params.get("dev_lab_results_state");
    return state === "empty" || state === "filled" ? state : null;
  } catch {
    return null;
  }
}

function getDevPreviewLabResultName(): string | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const value = new URLSearchParams(window.location.search).get(
      "dev_preview_lab_result",
    );
    return value?.trim().toLowerCase() || null;
  } catch {
    return null;
  }
}

function getDevPreviewLabContext(): "baseline" | "unknown" {
  if (!__DEV__ || typeof window === "undefined") return "baseline";
  try {
    return new URLSearchParams(window.location.search).get(
      "dev_lab_context",
    ) === "unknown"
      ? "unknown"
      : "baseline";
  } catch {
    return "baseline";
  }
}

function getDevPreviewInterpretationContext(
  context: ResultInterpretationContext,
): ResultInterpretationContext {
  return getDevPreviewLabContext() === "unknown"
    ? {
        cycleDay: null,
        cyclePhase: "unknown",
        treatmentPhase: null,
        usedContext: "unknown",
      }
    : context;
}

const DEV_FSH_INTERPRETATION_CONTEXT = getDevPreviewInterpretationContext({
  cycleDay: 3,
  cyclePhase: "baseline",
  treatmentPhase: "pre_treatment",
  usedContext: "baseline",
});

const DEV_MONITORING_INTERPRETATION_CONTEXT =
  getDevPreviewInterpretationContext({
    cycleDay: 8,
    cyclePhase: "follicular",
    treatmentPhase: "protocol",
    usedContext: "stimulation",
  });

const DEV_PREVIEW_LAB_RESULTS: DisplayLabResult[] = [
  DEV_PREVIEW_AMH_RESULT,
  {
    id: "dev-preview-fsh",
    name: "FSH",
    value: "7.1",
    unit: "mIU/mL",
    date: "May 19",
    sourceType: "blood",
    cycleId: "dev-cycle-current",
    cycleLabel: "Current cycle",
    trend: "none",
    category: getDevPreviewCategory(
      "FSH",
      "7.1",
      "mIU/mL",
      DEV_FSH_INTERPRETATION_CONTEXT,
    ),
    interpretationContext: DEV_FSH_INTERPRETATION_CONTEXT,
    ...(getBiomarkerFactsheet("FSH") ?? {}),
  },
  {
    id: "dev-preview-vitamin-d",
    name: "Vitamin D",
    value: "24",
    unit: "ng/mL",
    date: "May 20",
    status: "waiting",
    sourceType: "blood",
    cycleId: "dev-cycle-current",
    cycleLabel: "Current cycle",
    trend: "none",
    category: getDevPreviewCategory("Vitamin D", "24", "ng/mL"),
    ...(getBiomarkerFactsheet("Vitamin D") ?? {}),
  },
  {
    id: "dev-preview-endometrial-thickness",
    name: "Endometrial Thickness",
    value: "8.2",
    unit: "mm",
    date: "May 24",
    sourceType: "imaging",
    cycleId: "dev-cycle-current",
    cycleLabel: "Current cycle",
    trend: "up",
    category: getDevPreviewCategory(
      "Endometrial Thickness",
      "8.2",
      "mm",
      DEV_MONITORING_INTERPRETATION_CONTEXT,
    ),
    interpretationContext: DEV_MONITORING_INTERPRETATION_CONTEXT,
    ...(getBiomarkerFactsheet("Endometrial Thickness") ?? {}),
  },
  {
    id: "dev-preview-follicle-count",
    name: "Follicle Count (AFC)",
    value: "12",
    unit: "follicles",
    date: "May 24",
    sourceType: "imaging",
    cycleId: "dev-cycle-current",
    cycleLabel: "Current cycle",
    trend: "up",
    category: getDevPreviewCategory(
      "Follicle Count (AFC)",
      "12",
      "follicles",
      DEV_MONITORING_INTERPRETATION_CONTEXT,
    ),
    interpretationContext: DEV_MONITORING_INTERPRETATION_CONTEXT,
    ...(getBiomarkerFactsheet("Follicle Count (AFC)") ?? {}),
  },
  {
    id: "dev-preview-tsh",
    name: "TSH",
    value: "",
    unit: "",
    date: "May 22",
    status: "scheduled",
    sourceType: "blood",
    cycleId: "dev-cycle-past",
    cycleLabel: "IVF Cycle 1",
    trend: "none",
    category: null,
    ...(getBiomarkerFactsheet("TSH") ?? {}),
  },
];

const MANUAL_RESULT_TYPES: {
  type: TestResultType;
  labelKey: string;
  icon: LucideIcon;
}[] = [
  {
    type: "blood",
    labelKey: "all_lab_results.manual_type_blood",
    icon: TestTube2,
  },
  {
    type: "imaging",
    labelKey: "all_lab_results.manual_type_imaging",
    icon: ScanLine,
  },
  {
    type: "genetics",
    labelKey: "all_lab_results.manual_type_genetics",
    icon: Dna,
  },
  {
    type: "semenFluidAnalysis",
    labelKey: "all_lab_results.manual_type_semen",
    icon: ClipboardList,
  },
  {
    type: "urine",
    labelKey: "all_lab_results.manual_type_urine",
    icon: FlaskConical,
  },
  {
    type: "clinical",
    labelKey: "all_lab_results.manual_type_other",
    icon: FileText,
  },
];

const LAB_RESULT_TYPE_FILTERS: {
  id: LabResultTypeFilterId;
  labelKey: string;
  icon?: LucideIcon;
}[] = [
  { id: "blood", labelKey: "all_lab_results.filter_blood", icon: TestTube2 },
  { id: "imaging", labelKey: "all_lab_results.filter_imaging", icon: ScanLine },
  { id: "genetics", labelKey: "all_lab_results.filter_genetics", icon: Dna },
  {
    id: "semenFluidAnalysis",
    labelKey: "all_lab_results.filter_semen",
    icon: ClipboardList,
  },
  { id: "urine", labelKey: "all_lab_results.filter_urine", icon: FlaskConical },
  {
    id: "pathology",
    labelKey: "all_lab_results.filter_pathology",
    icon: Microscope,
  },
  { id: "other", labelKey: "all_lab_results.filter_other", icon: FileText },
];

const LAB_RESULT_STATUS_FILTERS: {
  id: LabResultStatusFilterId;
  labelKey: string;
  icon?: LucideIcon;
}[] = [
  {
    id: "resulted",
    labelKey: "all_lab_results.filter_resulted",
    icon: TestTube2,
  },
  {
    id: "pending",
    labelKey: "all_lab_results.filter_pending",
    icon: ClipboardList,
  },
  {
    id: "needs_review",
    labelKey: "all_lab_results.filter_needs_review",
    icon: AlertCircle,
  },
];

function getResultPurpose(result: LabResult, t: TranslationFunction) {
  const normalName = result.name.toLowerCase();
  if (normalName.includes("amh")) {
    return t("all_lab_results.purpose_amh");
  }
  if (normalName.includes("fsh")) {
    return t("all_lab_results.purpose_fsh");
  }
  if (normalName.includes("vitamin d")) {
    return t("all_lab_results.purpose_vitamin_d");
  }
  if (normalName.includes("tsh") || normalName.includes("thyroid")) {
    return t("all_lab_results.purpose_tsh");
  }
  return result.description ?? t("all_lab_results.purpose_default");
}

function labResultToInsight(
  result: LabResult,
  t: TranslationFunction,
): ResultInterpretation {
  return {
    testName: result.name,
    testPurpose: getResultPurpose(result, t),
    value: result.value,
    unit: result.unit,
    category: result.category,
    headline: result.isAbnormal
      ? t("all_lab_results.needs_review", { params: { name: result.name } })
      : result.name,
    interpretation: result.description ?? "",
    whatThisMeans: result.whyItMatters ?? "",
    nextStep: result.normalRange
      ? t("all_lab_results.compare_with_range", {
          params: { range: result.normalRange },
        })
      : undefined,
  };
}

function getResultRowBadge(
  result: LabResult,
  t: TranslationFunction,
): DocumentRowBadge | undefined {
  if (result.status === "waiting" || /waiting/i.test(result.date)) {
    return { label: t("all_lab_results.badge_waiting"), tone: "gold" };
  }
  if (result.status === "scheduled" || /scheduled/i.test(result.date)) {
    return { label: t("all_lab_results.badge_scheduled"), tone: "neutral" };
  }
  return undefined;
}

function isResultPending(result: LabResult) {
  return (
    result.status === "waiting" ||
    result.status === "scheduled" ||
    /waiting|scheduled/i.test(result.date)
  );
}

function inferResultType(result: DisplayLabResult): TestResultType | null {
  if (result.sourceType) return result.sourceType;

  // A result carrying a clinical code knows what it was taken from, so trust
  // that before guessing from words. The guess below is ordered, and "DNA
  // fragmentation index in Semen by Flow cytometry" hits the genetics rule
  // on `dna` before the semen rule can see `semen` — a spermiogram value
  // filed under Genetics, where the Sperm filter never finds it.
  //
  // Deliberately keyed on the code, not on `dataPointId`: that id can also
  // come from a bidirectional-substring match on the label, under which a
  // row named plainly "Volume" resolves to semen volume. Trusting it here
  // would move a urinalysis into the Sperm filter.
  const codedDataPoint = getDataPointForLoincCode(result.loincCode);
  const sampleTypes = codedDataPoint
    ? getDataPointSampleTypes(codedDataPoint.id)
    : [];
  if (sampleTypes.includes("semen")) return "semenFluidAnalysis";
  if (sampleTypes.includes("urine")) return "urine";

  const name = result.name.toLowerCase();
  if (
    /\b(amh|fsh|lh|tsh|hcg|estradiol|progesterone|vitamin d|prolactin|cbc|glucose|thyroid)\b/i.test(
      name,
    )
  ) {
    return "blood";
  }
  if (
    /\b(ultrasound|sono|scan|mri|x-ray|xray|ct|afc|follicle|endometrium|hsg)\b/i.test(
      name,
    )
  ) {
    return "imaging";
  }
  if (/\b(genetic|carrier|karyotype|dna|pgta|pgt-a|pgs)\b/i.test(name)) {
    return "genetics";
  }
  if (/\b(semen|sperm|motility|morphology)\b/i.test(name)) {
    return "semenFluidAnalysis";
  }
  if (/\b(urine|urinalysis)\b/i.test(name)) {
    return "urine";
  }
  return null;
}

function labResultMatchesTypeFilter(
  result: DisplayLabResult,
  filterId: LabResultTypeFilterId,
) {
  if (filterId === "all") return true;
  const type = inferResultType(result);
  if (filterId === "pathology") return type === "biopsy" || type === "sampling";
  if (filterId === "other") return type === "clinical" || type === null;
  return type === filterId;
}

function labResultMatchesStatusFilter(
  result: DisplayLabResult,
  filterId: LabResultStatusFilterId,
) {
  if (filterId === "all") return true;
  if (filterId === "resulted") return !isResultPending(result);
  if (filterId === "pending") return isResultPending(result);
  return !!result.isAbnormal;
}

function labResultMatchesSearch(result: DisplayLabResult, query: string) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return [
    result.name,
    result.value,
    result.unit,
    result.date,
    result.cycleLabel,
  ]
    .filter(Boolean)
    .some((field) => field!.toLowerCase().includes(normalized));
}

function getCycleFilterCycleId(filterId: LabResultCycleFilterId) {
  return filterId.startsWith("cycle:") ? filterId.slice("cycle:".length) : null;
}

function testResultMatchesCycleFilter(
  result: TestResult,
  filterId: LabResultCycleFilterId,
  currentCycleId: string | null,
) {
  if (filterId === "all") return true;
  if (filterId === "current") {
    return !!currentCycleId && result.cycleId === currentCycleId;
  }
  if (filterId === "unlinked") return !result.cycleId;
  return result.cycleId === getCycleFilterCycleId(filterId);
}

function displayResultMatchesCycleFilter(
  result: DisplayLabResult,
  filterId: LabResultCycleFilterId,
  currentCycleId: string | null,
) {
  if (filterId === "all") return true;
  if (filterId === "current") {
    return !!currentCycleId && result.cycleId === currentCycleId;
  }
  if (filterId === "unlinked") return !result.cycleId;
  return result.cycleId === getCycleFilterCycleId(filterId);
}

function buildDisplayLabResults(
  sourceResults: TestResult[],
  abbrev: (date: string) => string,
  t: TranslationFunction,
  locale: Locale,
  cycleLabelById: Record<string, string>,
  categoryResolver: ResultCategoryResolver,
): DisplayLabResult[] {
  const unknownLabel = t("all_lab_results.name_unknown");
  return groupCanonicalTestResults(sourceResults).map((historyGroup) => {
    const latest = historyGroup.latest;
    const latestContext = categoryResolver.contextFor(latest);
    const history = historyGroup.results.map((result) => ({
      id: result.id,
      date: result.date ? abbrev(result.date) : "",
      value: result.value,
      category: categoryResolver.categorize(result)?.category ?? null,
    }));
    const factsheet = latest.name
      ? getBiomarkerFactsheet(latest.name, locale)
      : null;
    return {
      id: latest.id,
      dataPointId: latest.dataPointId,
      testId:
        getCanonicalTestIdForDataPoint(latest.dataPointId) ??
        getCanonicalTestIdForMarkerName(latest.name),
      name: latest.name ?? unknownLabel,
      value: latest.value,
      unit: latest.unit ?? "",
      date: latest.date ? abbrev(latest.date) : "",
      ...(factsheet ?? {}),
      sourceType: latest.type,
      cycleId: latest.cycleId ?? null,
      cycleLabel: latest.cycleId
        ? (cycleLabelById[latest.cycleId] ?? null)
        : null,
      cycleLink: latest.cycleId
        ? {
            label:
              cycleLabelById[latest.cycleId] ??
              t("all_lab_results.filter_cycle_fallback"),
            cycleId: latest.cycleId,
          }
        : undefined,
      trend: historyGroup.trend,
      clinicalAssessment: latest.clinicalAssessment,
      category:
        categoryResolver.categorize(latest, latestContext)?.category ?? null,
      isAbnormal:
        latest.clinicalAssessment === "low" ||
        latest.clinicalAssessment === "high",
      history: history.length > 1 ? history : undefined,
      interpretationContext:
        latest.name && isCycleContextSensitiveTest(latest.name)
          ? latestContext
          : undefined,
      provenance: latest.source,
      attachment: latest.source.attachment,
    };
  });
}

function localizeLabResultFactsheet(
  result: DisplayLabResult,
  locale: Locale,
): DisplayLabResult {
  return {
    ...result,
    testId:
      result.testId ??
      getCanonicalTestIdForDataPoint(result.dataPointId) ??
      getCanonicalTestIdForMarkerName(result.name),
    ...(getBiomarkerFactsheet(result.name, locale) ?? {}),
    cycleLink:
      result.cycleLink ??
      (result.cycleId && result.cycleLabel
        ? { label: result.cycleLabel, cycleId: result.cycleId }
        : undefined),
  };
}

function getCycleSortValue(cycle: Cycle) {
  return new Date(cycle.startDate ?? cycle.createdAt ?? "").getTime();
}

function buildCycleLabels(
  cycles: Cycle[],
  cycleTypeLabels: Partial<Record<CycleType, string>>,
) {
  const sorted = [...cycles].sort(
    (a, b) => getCycleSortValue(a) - getCycleSortValue(b),
  );
  const countByType: Partial<Record<CycleType, number>> = {};
  const indexById: Record<string, number> = {};

  for (const cycle of sorted) {
    countByType[cycle.cycleType] = (countByType[cycle.cycleType] ?? 0) + 1;
    indexById[cycle.id] = countByType[cycle.cycleType]!;
  }

  return Object.fromEntries(
    cycles.map((cycle) => {
      const typeLabel =
        cycleTypeLabels[cycle.cycleType] ??
        CYCLE_TYPE_LABELS[cycle.cycleType] ??
        "Cycle";
      const shouldNumber = (countByType[cycle.cycleType] ?? 0) > 1;
      return [
        cycle.id,
        shouldNumber ? `${typeLabel} ${indexById[cycle.id] ?? 1}` : typeLabel,
      ];
    }),
  );
}

function buildCycleFilterOptions(
  results: DisplayLabResult[],
  cycles: Cycle[],
  currentCycleId: string | null,
  cycleLabelById: Record<string, string>,
  t: TranslationFunction,
): LabResultFilterOption<LabResultCycleFilterId>[] {
  const cycleIds = Array.from(
    new Set(
      results
        .map((result) => result.cycleId)
        .filter((cycleId): cycleId is string => !!cycleId),
    ),
  );
  const hasUnlinked = results.some((result) => !result.cycleId);
  if (cycleIds.length + (hasUnlinked ? 1 : 0) <= 1) return [];

  const cycleSortOrder = new Map(
    [...cycles]
      .sort((a, b) => getCycleSortValue(a) - getCycleSortValue(b))
      .map((cycle, index) => [cycle.id, index]),
  );
  const options: LabResultFilterOption<LabResultCycleFilterId>[] = [
    {
      id: "all",
      label: t("all_lab_results.filter_all_cycles"),
      icon: Calendar,
    },
  ];

  if (currentCycleId && cycleIds.includes(currentCycleId)) {
    options.push({
      id: "current",
      label: t("all_lab_results.filter_current_cycle"),
      icon: Calendar,
    });
  }

  cycleIds
    .filter((cycleId) => cycleId !== currentCycleId)
    .sort((a, b) => {
      const aOrder = cycleSortOrder.get(a) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = cycleSortOrder.get(b) ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.localeCompare(b);
    })
    .forEach((cycleId) => {
      const displayLabel =
        results.find((result) => result.cycleId === cycleId)?.cycleLabel ??
        cycleLabelById[cycleId] ??
        t("all_lab_results.filter_cycle_fallback");
      options.push({
        id: `cycle:${cycleId}`,
        label: displayLabel,
        icon: Calendar,
      });
    });

  if (hasUnlinked) {
    options.push({
      id: "unlinked",
      label: t("all_lab_results.filter_no_cycle"),
      icon: Calendar,
    });
  }

  return options;
}

function LabResultFilterRail({
  options,
}: {
  options: LabResultFilterChipOption[];
}) {
  if (options.length === 0) return null;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={{ marginHorizontal: -16, marginBottom: 16 }}
      contentContainerStyle={{
        gap: 8,
        paddingLeft: 16,
        paddingRight: 24,
        paddingBottom: 2,
      }}
    >
      {options.map((option) => (
        <MoonFilterChip
          key={option.id}
          label={option.label}
          icon={option.icon}
          selected={option.selected}
          onPress={option.onPress}
          tone="glass"
          showCheck={false}
        />
      ))}
    </ScrollView>
  );
}

function LabResultsEmptyState({
  onUpload,
  onFilesSelected,
  onManualEntry,
}: {
  onUpload: () => void;
  onFilesSelected: (files: SelectedFile[]) => void;
  onManualEntry: () => void;
}) {
  const t = useTranslations();
  return (
    <View style={{ paddingVertical: 8, alignItems: "center" }}>
      <DocumentFileDropZone
        onPick={onUpload}
        onFilesSelected={onFilesSelected}
        dropHint={t("documents.empty.or_drop_files_here")}
      >
        <EmptyState
          density="compact"
          titleVariant="featured"
          illustration={
            <DialogIcon
              source={resultHistoryIllustration}
              width={68}
              height={68}
              style={{ marginTop: -6, marginBottom: -7 }}
              testID="lab-empty-illustration"
            />
          }
          title={t("all_lab_results.upload_any_result")}
          subtitle={t("all_lab_results.store_results_in_one_place")}
          subtitleStyle={{ maxWidth: 410 }}
          style={{ paddingVertical: 0 }}
          action={
            <View
              testID="lab-empty-actions"
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <CompactActionButton
                label={t("all_lab_results.upload_results")}
                action="all_lab_results.upload_results"
                leadingIcon={Upload}
                variant="primary"
                onPress={onUpload}
              />
              <CompactActionButton
                label={t("all_lab_results.empty_manual_entry")}
                action="all_lab_results.empty_manual_entry"
                leadingIcon={Edit3}
                variant="secondary"
                onPress={onManualEntry}
              />
            </View>
          }
        />
      </DocumentFileDropZone>
    </View>
  );
}

function getResultDateLabel(result: LabResult, t: TranslationFunction) {
  const date = result.date.trim();
  if (!date || /waiting|scheduled/i.test(date)) {
    return t("all_lab_results.no_date");
  }
  return date;
}

function getManualResultCategoryFields(type: TestResultType) {
  switch (type) {
    case "blood":
      return { bloodTestCategory: "other" as const };
    case "imaging":
      return { imagingTestCategory: "other" as const };
    case "genetics":
      return { geneticsTestCategory: "other" as const };
    case "semenFluidAnalysis":
      return { semenFluidTestCategory: "basic" as const };
    default:
      return {};
  }
}

function isNumericResultValue(value: string) {
  const normalized = value.trim().replace(",", ".");
  return normalized.length > 0 && Number.isFinite(Number(normalized));
}

function ManualLabResultModal({
  visible,
  onClose,
  onOpenUpload,
  initialName,
  initialType,
}: {
  visible: boolean;
  onClose: () => void;
  onOpenUpload: (files?: SelectedFile[]) => void;
  initialName?: string | null;
  initialType?: TestResultType | null;
}) {
  const t = useTranslations();
  const createTestResult = useCreateTestResult();
  const [type, setType] = useState<TestResultType>(initialType ?? "blood");
  const [name, setName] = useState(initialName ?? "");
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("");
  const [date, setDate] = useState(() => getTodayString());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const reset = useCallback(() => {
    setType(initialType ?? "blood");
    setName(initialName ?? "");
    setValue("");
    setUnit("");
    setDate(getTodayString());
    setErrorMessage(null);
  }, [initialName, initialType]);

  useEffect(() => {
    if (!visible) return;
    reset();
  }, [reset, visible]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();
    const trimmedValue = value.trim();
    const trimmedDate = date.trim();
    if (!trimmedName || !trimmedValue || !trimmedDate) {
      setErrorMessage(t("all_lab_results.manual_error_required"));
      return;
    }

    setErrorMessage(null);
    try {
      await createTestResult.mutateAsync({
        name: trimmedName,
        type,
        patientValue: trimmedValue,
        valueType: isNumericResultValue(trimmedValue) ? "numeric" : "string",
        unit: unit.trim() || null,
        date: trimmedDate,
        notes: t("all_lab_results.manual_notes"),
        ...getManualResultCategoryFields(type),
      });
      handleClose();
    } catch (error) {
      console.warn(
        "[AllLabResultsScreen] Failed to create test result:",
        error,
      );
      setErrorMessage(t("all_lab_results.manual_error_save"));
    }
  }, [createTestResult, date, handleClose, name, t, type, unit, value]);

  if (!visible) return null;

  const hasPresetName = Boolean(initialName?.trim());

  return (
    <Dialog
      visible
      onClose={handleClose}
      closeButton="minimal"
      closeAccessibilityLabel={t("learn.article_actions.close_accessibility")}
      closeAction="close_manual_lab_result"
    >
      <DialogHeader
        title={t("all_lab_results.manual_title")}
        subtitle={t("all_lab_results.manual_subtitle")}
        subtitleStyle={{ paddingRight: 24 }}
        style={{ marginBottom: 18 }}
      />

      <ScrollView
        style={{ flexShrink: 1 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <DocumentFileDropZone
          compact
          onPick={() => onOpenUpload()}
          onFilesSelected={onOpenUpload}
        />

        {!hasPresetName ? (
          <View style={{ marginTop: 16, marginBottom: 16 }}>
            <InlineOptionPicker
              options={MANUAL_RESULT_TYPES.map((option) => ({
                id: option.type,
                label: t(option.labelKey),
                icon: option.icon,
                action: "manual_lab_result_type",
              }))}
              value={type}
              onChange={(next) => {
                const match = MANUAL_RESULT_TYPES.find(
                  (option) => option.type === next,
                );
                if (match) setType(match.type);
              }}
              wrap
            />
          </View>
        ) : null}

        {/* The value carries its unit inside the field, as when a
            result is edited; the name and date use the same box. */}
        <View style={{ gap: 10, marginTop: hasPresetName ? 16 : 0 }}>
          <MoonValueInput
            value={name}
            onChangeText={setName}
            placeholder={t("all_lab_results.manual_name_placeholder")}
            accessibilityLabel={t("all_lab_results.manual_name_placeholder")}
          />
          <LabValueField
            value={value}
            onChangeValue={setValue}
            unit={unit}
            onChangeUnit={setUnit}
            marker={name}
            placeholder={t("all_lab_results.manual_value_placeholder")}
            accessibilityLabel={t("all_lab_results.manual_value_placeholder")}
          />
          <DateField
            label={t("date_picker.enter_date")}
            value={date}
            onChange={setDate}
          />
        </View>

        {errorMessage ? (
          <ActionBanner
            variant="important"
            message={errorMessage}
            style={{ marginTop: 12 }}
          />
        ) : null}
      </ScrollView>

      <DialogActions style={{ marginTop: 16 }}>
        <PrimaryButton
          label={t("all_lab_results.manual_save")}
          action="all_lab_results.manual_save"
          onPress={handleSave}
          loading={createTestResult.isPending}
        />
      </DialogActions>
    </Dialog>
  );
}

export default function AllLabResultsScreen({
  navigation,
  route,
  results: propResults,
}: Props) {
  const visualEvidenceRoute =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("dev_visual_evidence") ===
      "true";
  const t = useTranslations();
  const capturePress = usePressCapture("AllLabResultsScreen");
  const { locale } = useLocale();
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const testResultsQuery = useTestResults();
  const { data: testResults, error: testResultsError } = testResultsQuery;
  const { data: cycles = [] } = useCycles();
  const cycleTypeLabels = useCycleTypeLabels();
  const { abbrev } = useDateFormat();
  const deleteTestResult = useDeleteTestResult();
  const updateTestResult = useUpdateTestResult();
  const devPreviewLabResults = getDevPreviewLabResultsEnabled();
  const devPreviewLabResultsState = getDevPreviewLabResultsState();
  const devPreviewLabResultName = getDevPreviewLabResultName();
  const devClinicalDataState = getDevClinicalDataState("labs");
  const openTestFactsheet = (testId: string) => {
    setSelectedResult(null);
    const target = getTestRouteTarget(testId);
    navigation.getParent()?.dispatch(
      CommonActions.navigate({
        name: "Access",
        params: {
          screen: target.screen,
          params: { ...target.params, returnTab: "Care" },
        },
      }),
    );
  };
  const labsDataState = devClinicalDataState
    ? devClinicalDataState
    : devPreviewLabResults || propResults !== undefined
      ? null
      : testResultsError
        ? testResults === undefined
          ? "unavailable"
          : "stale"
        : null;
  const displayLabResultsLoading =
    !devPreviewLabResults &&
    propResults === undefined &&
    testResultsQuery.isLoading;
  const retryLabResults = () => {
    void testResultsQuery.refetch();
  };
  const cycleIdFilter = route.params?.cycleId ?? null;
  const cycleLabel = route.params?.cycleLabel ?? null;
  const initialResultId = route.params?.initialResultId ?? null;
  const [search, setSearch] = useState("");
  const [activeTypeFilter, setActiveTypeFilter] =
    useState<LabResultTypeFilterId>("all");
  const [activeStatusFilter, setActiveStatusFilter] =
    useState<LabResultStatusFilterId>("all");
  const [activeCycleFilter, setActiveCycleFilter] =
    useState<LabResultCycleFilterId>("all");
  const [selectedResult, setSelectedResult] = useState<DisplayLabResult | null>(
    null,
  );
  const devPreviewResultOpened = useRef(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<SelectedFile[]>(
    [],
  );
  const [manualEntryVisible, setManualEntryVisible] = useState(false);
  const [manualEntryName, setManualEntryName] = useState<string | null>(null);
  const [manualEntryType, setManualEntryType] = useState<TestResultType | null>(
    null,
  );
  const uploadButtonDrift = useRef(new Animated.Value(0)).current;

  const openManualEntry = useCallback(
    (name?: string, type?: TestResultType | null) => {
      setManualEntryName(name ?? null);
      setManualEntryType(type ?? null);
      setManualEntryVisible(true);
    },
    [],
  );

  const currentCycleId = useMemo(
    () => cycles.find((cycle) => cycle.status === "open")?.id ?? null,
    [cycles],
  );
  const cycleLabelById = useMemo(
    () => buildCycleLabels(cycles, cycleTypeLabels),
    [cycles, cycleTypeLabels],
  );
  const sourceTestResults = useMemo(() => {
    if (!testResults) return [];
    // Nameless rows don't resolve to an openable biomarker — they would render
    // as an "Unknown" row and inflate the header count — so we drop them from
    // the screen entirely. The Care badge applies the same filter.
    const named = testResults.filter((result) => result.name);
    return cycleIdFilter
      ? named.filter((result) => result.cycleId === cycleIdFilter)
      : named;
  }, [cycleIdFilter, testResults]);
  const shouldUseDevResults =
    devPreviewLabResultsState === "filled" ||
    (devPreviewLabResults && (!testResults || testResults.length === 0));
  const categoryResolver = useResultCategoryResolver();

  // Derive lab results from API if no prop results provided.
  const baseResults: DisplayLabResult[] = useMemo(() => {
    if (devPreviewLabResultsState === "empty") return [];
    if (propResults && propResults.length > 0) {
      return propResults.map((result) =>
        localizeLabResultFactsheet(result, locale),
      );
    }
    if (shouldUseDevResults) {
      return DEV_PREVIEW_LAB_RESULTS.map((result) =>
        localizeLabResultFactsheet(result, locale),
      );
    }
    if (!testResults) return [];
    return buildDisplayLabResults(
      sourceTestResults,
      abbrev,
      t,
      locale,
      cycleLabelById,
      categoryResolver,
    );
  }, [
    propResults,
    devPreviewLabResultsState,
    shouldUseDevResults,
    testResults,
    sourceTestResults,
    abbrev,
    cycleLabelById,
    categoryResolver,
    locale,
    t,
  ]);

  const cycleFilterOptions = useMemo(
    () =>
      cycleIdFilter
        ? []
        : buildCycleFilterOptions(
            baseResults,
            cycles,
            currentCycleId,
            cycleLabelById,
            t,
          ),
    [baseResults, cycleIdFilter, cycles, currentCycleId, cycleLabelById, t],
  );

  useEffect(() => {
    if (cycleIdFilter && activeCycleFilter !== "all") {
      setActiveCycleFilter("all");
      return;
    }
    if (
      activeCycleFilter !== "all" &&
      !cycleFilterOptions.some((option) => option.id === activeCycleFilter)
    ) {
      setActiveCycleFilter("all");
    }
  }, [activeCycleFilter, cycleFilterOptions, cycleIdFilter]);

  const results: DisplayLabResult[] = useMemo(() => {
    if (activeCycleFilter === "all" || cycleIdFilter) return baseResults;
    if (
      !devPreviewLabResultsState &&
      (!propResults || propResults.length === 0) &&
      testResults
    ) {
      const cycleScopedResults = sourceTestResults.filter((result) =>
        testResultMatchesCycleFilter(result, activeCycleFilter, currentCycleId),
      );
      return buildDisplayLabResults(
        cycleScopedResults,
        abbrev,
        t,
        locale,
        cycleLabelById,
        categoryResolver,
      );
    }
    return baseResults.filter((result) =>
      displayResultMatchesCycleFilter(
        result,
        activeCycleFilter,
        currentCycleId,
      ),
    );
  }, [
    abbrev,
    activeCycleFilter,
    baseResults,
    categoryResolver,
    currentCycleId,
    cycleIdFilter,
    cycleLabelById,
    devPreviewLabResultsState,
    locale,
    propResults,
    sourceTestResults,
    t,
    testResults,
  ]);

  useEffect(() => {
    if (!initialResultId || selectedResult) return;
    const result = results.find(
      (candidate) => candidate.id === initialResultId,
    );
    if (result) setSelectedResult(result);
  }, [initialResultId, results, selectedResult]);

  useEffect(() => {
    if (!devPreviewLabResultName || devPreviewResultOpened.current) return;
    const result = results.find(
      (candidate) =>
        candidate.id.toLowerCase() === devPreviewLabResultName ||
        candidate.name.toLowerCase() === devPreviewLabResultName,
    );
    if (!result) return;
    devPreviewResultOpened.current = true;
    setSelectedResult(result);
  }, [devPreviewLabResultName, results]);

  const typeFilterOptions = useMemo(
    () =>
      LAB_RESULT_TYPE_FILTERS.map((filter) => ({
        id: filter.id,
        label: t(filter.labelKey),
        icon: filter.icon,
      })),
    [t],
  );
  const statusFilterOptions = useMemo(
    () =>
      LAB_RESULT_STATUS_FILTERS.map((filter) => ({
        id: filter.id,
        label: t(filter.labelKey),
        icon: filter.icon,
      })),
    [t],
  );

  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(uploadButtonDrift, {
          toValue: 1,
          duration: 2600,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(uploadButtonDrift, {
          toValue: 0,
          duration: 2600,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [uploadButtonDrift, reducedMotion]);

  const handleUpload = useCallback(() => {
    setPendingUploadFiles([]);
    setUploadModalVisible(true);
  }, []);

  const handleDroppedUpload = useCallback((files: SelectedFile[]) => {
    setPendingUploadFiles(files);
    setUploadModalVisible(true);
  }, []);

  const handleManualUpload = useCallback(
    (files?: SelectedFile[]) => {
      setManualEntryVisible(false);
      if (files && files.length > 0) {
        handleDroppedUpload(files);
        return;
      }
      handleUpload();
    },
    [handleDroppedUpload, handleUpload],
  );

  const handleUploadClose = useCallback(() => {
    setUploadModalVisible(false);
    setPendingUploadFiles([]);
  }, []);

  const clearFilters = useCallback(() => {
    setSearch("");
    setActiveCycleFilter("all");
    setActiveTypeFilter("all");
    setActiveStatusFilter("all");
  }, []);

  const filtered = results.filter(
    (result) =>
      labResultMatchesSearch(result, search) &&
      labResultMatchesTypeFilter(result, activeTypeFilter) &&
      labResultMatchesStatusFilter(result, activeStatusFilter),
  );
  const filtersActive =
    search.trim().length > 0 ||
    activeTypeFilter !== "all" ||
    activeStatusFilter !== "all" ||
    (!cycleIdFilter && activeCycleFilter !== "all");
  // Markers = grouped rows the screen renders; measurements = raw rows inside.
  // When a filter narrows the list, the measurement total loses its meaning
  // (filters apply per marker, not per measurement), so we only show the
  // "N of M markers" shape in that case. The measurement count is read from
  // the displayed rows so it stays in sync with cycle/search/type/status
  // narrowing — a raw `sourceTestResults.length` would double-count rows the
  // active cycle filter had already hidden.
  const measurementCount = filtered.reduce(
    (total, result) => total + (result.history?.length ?? 1),
    0,
  );
  const markersText = t(
    filtered.length === 1
      ? "all_lab_results.markers_tested_one"
      : "all_lab_results.markers_tested_other",
    { params: { count: String(filtered.length) } },
  );
  const measurementsText = t(
    measurementCount === 1
      ? "all_lab_results.measurements_one"
      : "all_lab_results.measurements_other",
    { params: { count: String(measurementCount) } },
  );
  const countLabel =
    filtersActive && filtered.length !== baseResults.length
      ? t("all_lab_results.markers_tested_filtered", {
          params: {
            count: String(filtered.length),
            total: String(baseResults.length),
          },
        })
      : `${markersText} · ${measurementsText}`;
  const noResultsKey = filtersActive
    ? "all_lab_results.no_results_filtered"
    : "all_lab_results.no_results_found";
  const showFilters = baseResults.length > 0;
  const showStatusFilters =
    baseResults.some(isResultPending) ||
    baseResults.some((result) => result.isAbnormal);

  const onTypeFilterChange = useCallback((id: LabResultTypeFilterId) => {
    setActiveTypeFilter((current) => (current === id ? "all" : id));
  }, []);
  const onStatusFilterChange = useCallback((id: LabResultStatusFilterId) => {
    setActiveStatusFilter((current) => (current === id ? "all" : id));
  }, []);
  const onCycleFilterChange = useCallback((id: LabResultCycleFilterId) => {
    setActiveCycleFilter((current) => (current === id ? "all" : id));
  }, []);

  const visibleFilterOptions = useMemo<LabResultFilterChipOption[]>(() => {
    const options: LabResultFilterChipOption[] = [
      {
        id: "all",
        label: t("all_lab_results.filter_all"),
        selected: !filtersActive,
        onPress: clearFilters,
      },
    ];

    for (const option of cycleFilterOptions) {
      if (option.id === "all") continue;
      options.push({
        ...option,
        selected: activeCycleFilter === option.id,
        onPress: () => onCycleFilterChange(option.id),
      });
    }

    for (const option of typeFilterOptions) {
      options.push({
        ...option,
        selected: activeTypeFilter === option.id,
        onPress: () => onTypeFilterChange(option.id),
      });
    }

    if (showStatusFilters) {
      for (const option of statusFilterOptions) {
        options.push({
          ...option,
          selected: activeStatusFilter === option.id,
          onPress: () => onStatusFilterChange(option.id),
        });
      }
    }

    return options;
  }, [
    activeCycleFilter,
    activeStatusFilter,
    activeTypeFilter,
    clearFilters,
    cycleFilterOptions,
    filtersActive,
    onCycleFilterChange,
    onStatusFilterChange,
    onTypeFilterChange,
    showStatusFilters,
    statusFilterOptions,
    t,
    typeFilterOptions,
  ]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View className="flex-row items-start justify-between">
          <BackLink
            label={t("all_lab_results.back")}
            action="all_lab_results.back"
            onPress={handleBack}
            style={{ marginBottom: 8 }}
          />
        </View>

        <View style={{ marginBottom: 24 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 38,
              fontWeight: "700",
              color: "#3B1A08",
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
                backgroundSize: "200px 200px, 100% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            }}
          >
            {cycleLabel
              ? t("all_lab_results.title_for_cycle", {
                  params: { cycle: cycleLabel },
                })
              : t("all_lab_results.title_all")}
          </Text>
          {labsDataState !== "unavailable" ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                lineHeight: 19,
                color: EMBER.colors.textLight,
                marginTop: 8,
              }}
            >
              {countLabel}
            </Text>
          ) : null}
        </View>

        {/* Search bar + upload */}
        {labsDataState !== "unavailable" ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              marginBottom: showFilters ? 12 : 16,
            }}
          >
            <MoonSearchInput
              value={search}
              onChangeText={setSearch}
              placeholder={t("all_lab_results.search_placeholder")}
              onClear={() => setSearch("")}
              tone="glass"
              style={{ flex: 1, borderRadius: 12 }}
            />
            <Pressable
              onPress={() => {
                // Icon-only upload control with no visible copy to name it.
                capturePress("upload_lab_result");
                handleUpload();
              }}
              style={{
                ...cardSurface(),
                width: 50,
                height: 44,
                borderRadius: 12,
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              }}
            >
              <Animated.Image
                source={require("@src/apps/moon/assets/images/care-hero-glow-gradient.png")}
                style={{
                  position: "absolute",
                  left: -7,
                  top: -8,
                  width: 64,
                  height: 60,
                  opacity: 1,
                  transform: [
                    {
                      translateX: uploadButtonDrift.interpolate({
                        inputRange: [0, 1],
                        outputRange: [-5, 6],
                      }),
                    },
                    {
                      translateY: uploadButtonDrift.interpolate({
                        inputRange: [0, 1],
                        outputRange: [3, -4],
                      }),
                    },
                    {
                      scale: uploadButtonDrift.interpolate({
                        inputRange: [0, 1],
                        outputRange: [1.02, 1.08],
                      }),
                    },
                  ],
                }}
                resizeMode="cover"
              />
              <View style={{ zIndex: 2, elevation: 2 }}>
                <Plus size={19} color="#fff" strokeWidth={2.25} />
              </View>
            </Pressable>
          </View>
        ) : null}

        {labsDataState !== "unavailable" && showFilters ? (
          <LabResultFilterRail options={visibleFilterOptions} />
        ) : null}

        {/* Results */}
        <View style={{ gap: 9 }}>
          {labsDataState === "unavailable" ? (
            <ClinicalDataStatus
              state="unavailable"
              title={t("clinical_data.unavailable_title")}
              message={t("clinical_data.unavailable_body")}
              retryLabel={t("clinical_data.retry")}
              retryingLabel={t("clinical_data.retrying")}
              onRetry={retryLabResults}
              isRetrying={testResultsQuery.isFetching}
              testID="lab-results-unavailable"
            />
          ) : (
            <>
              {labsDataState === "stale" ? (
                <ClinicalDataStatus
                  state="stale"
                  message={t("clinical_data.stale_body")}
                  retryLabel={t("clinical_data.retry")}
                  retryingLabel={t("clinical_data.retrying")}
                  onRetry={retryLabResults}
                  isRetrying={testResultsQuery.isFetching}
                  testID="lab-results-stale"
                />
              ) : null}
              {displayLabResultsLoading
                ? Array.from({ length: 4 }).map((_, index) => (
                    <ListRowSkeleton
                      key={index}
                      showLeadingSquare
                      showSubtitle
                      showTrailing
                    />
                  ))
                : null}
              {!displayLabResultsLoading &&
                filtered.map((result) => {
                  const insight = labResultToInsight(result, t);
                  return (
                    <DocumentTestResultRow
                      key={result.id}
                      title={insight.testName}
                      subtitle={insight.testPurpose}
                      dateLabel={getResultDateLabel(result, t)}
                      value={insight.value}
                      hideValue={!insight.value}
                      unit={insight.unit}
                      category={insight.category}
                      badge={getResultRowBadge(result, t)}
                      pending={isResultPending(result)}
                      pendingOpacity={result.status === "scheduled" ? 0.2 : 1}
                      onPress={() => setSelectedResult(result)}
                    />
                  );
                })}

              {!displayLabResultsLoading &&
                filtered.length === 0 &&
                baseResults.length === 0 && (
                  <LabResultsEmptyState
                    onUpload={handleUpload}
                    onFilesSelected={handleDroppedUpload}
                    onManualEntry={() => openManualEntry()}
                  />
                )}

              {!displayLabResultsLoading &&
                filtered.length === 0 &&
                baseResults.length > 0 && (
                  <SearchEmptyState
                    title={t(noResultsKey)}
                    onReset={filtersActive ? clearFilters : undefined}
                  />
                )}
            </>
          )}
        </View>
      </ScrollView>

      <TestResultDetailDialog
        result={selectedResult}
        point={
          selectedResult
            ? {
                id: selectedResult.id,
                date: selectedResult.date,
                value: selectedResult.value,
                category: selectedResult.category,
                clinicalAssessment: selectedResult.clinicalAssessment,
                clinicalAssessmentDetails:
                  selectedResult.clinicalAssessmentDetails,
                attachment: selectedResult.attachment,
                provenance: selectedResult.provenance,
              }
            : null
        }
        onClose={() => setSelectedResult(null)}
        onAttachSource={
          devPreviewLabResults
            ? undefined
            : async (id, attachmentId) => {
                await updateTestResult.mutateAsync({
                  testResultId: id,
                  data: { attachmentId },
                });
              }
        }
        cycleLink={selectedResult?.cycleLink}
        onOpenCycle={
          devPreviewLabResults
            ? undefined
            : (cycleLink) => {
                setSelectedResult(null);
                if (!cycleLink.cycleId) return;
                if (cycleLink.cycleId === currentCycleId) {
                  navigation.navigate("CycleDetail", {
                    cycleId: cycleLink.cycleId,
                  });
                } else {
                  navigation.navigate("PastCycleDetail", {
                    cycleId: cycleLink.cycleId,
                  });
                }
              }
        }
        onEdit={
          devPreviewLabResults
            ? undefined
            : async (id, data) => {
                await updateTestResult.mutateAsync({ testResultId: id, data });
                setSelectedResult(null);
              }
        }
        onDelete={
          devPreviewLabResults
            ? undefined
            : async (id) => {
                await deleteTestResult.mutateAsync(id);
                setSelectedResult(null);
              }
        }
        testId={selectedResult?.testId}
        onOpenTest={openTestFactsheet}
      />
      <DocumentUploadModal
        visible={uploadModalVisible}
        onClose={handleUploadClose}
        slot={null}
        defaultCategory="medical_records"
        defaultTags={["doctype:lab"]}
        initialFiles={pendingUploadFiles}
        autoPickOnOpen={!visualEvidenceRoute}
      />
      <ManualLabResultModal
        visible={manualEntryVisible}
        initialName={manualEntryName}
        initialType={manualEntryType}
        onOpenUpload={handleManualUpload}
        onClose={() => {
          setManualEntryVisible(false);
          setManualEntryName(null);
          setManualEntryType(null);
        }}
      />
    </GlassScreenLayout>
  );
}
